import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Swal from "sweetalert2";
import { BsEyeFill, BsPencilSquare, BsTrashFill } from 'react-icons/bs';
import { FaMapMarkedAlt } from "react-icons/fa"; // Import the map icon
import usericon from "./user.png";
import TablePagination from '@mui/material/TablePagination';
import NoData from "./NoData";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#ffffff03',
    color: theme.palette.common.black,
    fontWeight: 'bold',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'white',
  },
  '&:nth-of-type(even)': {
    backgroundColor: 'white',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export default function XTable({ head = [], rows = [], data, onEdit, onDelete, onView, onMap, onAction = () => { } }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const rowKeys = rows.length > 0 ? Object.keys(rows[0]) : [];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  // Calculate the total number of columns
  const totalColumns = head.length + ((onView || onEdit || onDelete || onMap) ? 1 : 0);

  return (
    <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff24', borderRadius: '16px', overflow: 'hidden' }}>
      <Table aria-label="customized table">
        <TableHead>
          <TableRow sx={{ color: '#ffffff24' }}>
            {head.map((h, index) => (
              <StyledTableCell key={index}>{h}</StyledTableCell>
            ))}
            {(onView || onEdit || onDelete || onMap) && <StyledTableCell>Actions</StyledTableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {!rows || rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalColumns} sx={{ border: 0, p: 0 }}>
                <NoData title="No Data Found" message="There are currently no records to display in this table. Try adjusting your search or filters." />
              </TableCell>
            </TableRow>
          ) : (
            <>
              {(rowsPerPage > 0
                ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : rows
              ).map((row, index) => (
                <StyledTableRow key={index}>
                  {rowKeys.map((key, cellIndex) => (
                    <StyledTableCell key={cellIndex} align="left">
                      {key.toLowerCase().includes('image') || key.toLowerCase().includes('photo') ? (
                        <img
                          src={row[key] || usericon}
                          alt=""
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (head[cellIndex] || '').toLowerCase() === 'preview' ? (
                        <img
                          src={row[key]}
                          alt="Preview"
                          style={{
                            width: '120px',
                            height: '90px',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        row[key]
                      )}
                    </StyledTableCell>
                  ))}
                  <StyledTableCell>
                    {onView && (
                      <IconButton onClick={() => onView(row)} size="small" aria-label="view">
                        <BsEyeFill fontSize={16} />
                      </IconButton>
                    )}
                    {/* New Map Icon Button */}
                    {onMap && (
                      <IconButton onClick={() => onMap(row)} size="small" aria-label="map">
                        <FaMapMarkedAlt fontSize={16} />
                      </IconButton>
                    )}
                    {onEdit && (
                      <IconButton onClick={() => onEdit(row)} size="small" aria-label="edit">
                        <BsPencilSquare fontSize={16} />
                      </IconButton>
                    )}
                    {onDelete && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          Swal.fire({
                            title: "Are you sure?",
                            text: "You won't be able to revert this!",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Yes, delete it!",
                          }).then((result) => {
                            if (result.isConfirmed) {
                               onDelete(row.id);
                            }
                          });
                        }}
                        size="small"
                        aria-label="delete"
                      >
                        <BsTrashFill fontSize={16} />
                      </IconButton>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
              {emptyRows > 0 && (
                <StyledTableRow style={{ height: 53 * emptyRows }}>
                  <StyledTableCell colSpan={totalColumns} />
                </StyledTableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
}